import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const questions = [
  {
    id: "idea_status",
    category: "Let's start",
    question: "Where are you at with your idea?",
    type: "radio",
    options: [
      { value: "have_idea", label: "i have an idea" },
      { value: "no_idea", label: "i dont have an idea" },
      { value: "want_new_idea", label: "i want a new idea" },
    ],
  },
  {
    id: "idea_description",
    category: "Your idea",
    question: "Describe your startup idea in ONE sentence",
    type: "textarea",
    placeholder: "keep it concise and specific...",
    skipIf: (answers) => answers.idea_status === "no_idea" || answers.idea_status === "want_new_idea",
  },
  {
    id: "experienced_problem",
    category: "Your idea",
    question: "Have you personally experienced this problem?",
    type: "radio",
    options: [
      { value: "true", label: "yes" },
      { value: "false", label: "no" },
    ],
    skipIf: (answers) => answers.idea_status === "no_idea" || answers.idea_status === "want_new_idea",
  },
  {
    id: "major_skillset",
    category: "Your edge",
    question: "What is your major skillset or expertise?",
    type: "textarea",
    placeholder: "e.g., software engineering, sales, design, domain expertise...",
  },
  {
    id: "unfair_advantage",
    category: "Your edge",
    question: "Rate your unfair advantage in this space (1-10)",
    type: "slider",
    min: 1,
    max: 10,
    labels: {
      1: "i have zero advantage anywhere",
      5: "i have theoretical knowledge",
      10: "bro i can build, raise and do everything in this space in 24hrs"
    }
  },
  {
    id: "startup_experience",
    category: "Career & edge",
    question: "Have you worked in a startup before?",
    type: "radio",
    options: [
      { value: "no", label: "no" },
      { value: "early stage", label: "early stage" },
      { value: "mid stage", label: "mid stage" },
      { value: "growth stage", label: "growth stage" },
    ],
  },
  {
    id: "worked_with_founders",
    category: "Career & edge",
    question: "Have you worked directly with founders?",
    type: "radio",
    options: [
      { value: "true", label: "yes" },
      { value: "false", label: "no" },
    ],
  },
  {
    id: "runway_months",
    category: "Financial reality",
    question: "How many months of savings/runway do you have?",
    type: "number",
    placeholder: "enter number of months",
  },
  {
    id: "debt_level",
    category: "Financial reality",
    question: "Do you currently have debt?",
    type: "radio",
    options: [
      { value: "none", label: "none" },
      { value: "manageable", label: "manageable" },
      { value: "stressful", label: "stressful" },
    ],
  },
  {
    id: "has_dependents",
    category: "Financial reality",
    question: "Do you have dependents?",
    type: "radio",
    options: [
      { value: "true", label: "yes" },
      { value: "false", label: "no" },
    ],
  },
  {
    id: "can_raise_from_network",
    category: "Network",
    question: "Could you raise money from 3 people you know today?",
    type: "radio",
    options: [
      { value: "true", label: "yes" },
      { value: "false", label: "no" },
    ],
  },
  {
    id: "network_strength",
    category: "Network",
    question: "Rate your network strength (1-10)",
    type: "slider",
    min: 1,
    max: 10,
    labels: {
      1: "i only know myself",
      5: "i have varun mayya, ganesh sonawane and rahul mathur on text",
      10: "i have nikhil kamath on speed dial"
    }
  },
  {
    id: "emotional_stability",
    category: "Emotional readiness",
    question: "Emotional stability right now (1-10)",
    type: "slider",
    min: 1,
    max: 10,
    labels: {
      1: "i rant on the internet everyday",
      5: "i cry when someone breaks up with me",
      10: "i laugh when people swear at me"
    }
  },
  {
    id: "comfort_with_uncertainty",
    category: "Emotional readiness",
    question: "How comfortable are you with uncertainty? (1-10)",
    type: "slider",
    min: 1,
    max: 10,
    labels: {
      1: "i cant work if you dont tell me EXACTLY what to do",
      5: "i cant work if 5 things are thrown at me",
      10: "i can tweet, handle the govt, face haters all at the same time. i live for chaos"
    }
  },
];

export default function Questionnaire() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);

  // Filter out skipped questions
  const getActiveQuestions = () => {
    return questions.filter(q => !q.skipIf || !q.skipIf(answers));
  };

  const activeQuestions = getActiveQuestions();

  // Adjust step index if skipped questions make the list shrink and step index becomes out of bounds
  useEffect(() => {
    if (currentStep >= activeQuestions.length && activeQuestions.length > 0) {
      setCurrentStep(activeQuestions.length - 1);
    }
  }, [answers, activeQuestions.length, currentStep]);

  const currentQuestion = activeQuestions[currentStep];
  const progress = activeQuestions.length > 0 ? ((currentStep + 1) / activeQuestions.length) * 100 : 0;

  // Initialize slider values with minimum value
  const getCurrentValue = () => {
    if (currentQuestion && currentQuestion.type === "slider") {
      return answers[currentQuestion.id] || currentQuestion.min.toString();
    }
    return answers[currentQuestion.id];
  };

  const handleAnswer = (value) => {
    setAnswers({ ...answers, [currentQuestion.id]: value });
  };

  const handleSliderChange = (value) => {
    handleAnswer(value[0].toString());
  };

  const canProceed = () => {
    if (!currentQuestion) return false;
    const value = answers[currentQuestion.id];
    if (currentQuestion.type === "slider") {
      return true;
    }
    return value !== undefined && value !== "";
  };

  const handleNext = () => {
    if (currentStep < activeQuestions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Transform answers to match backend schema
      const payload = {
        idea_status: answers.idea_status || "have_idea",
        idea_description: answers.idea_description || (answers.idea_status === "want_new_idea" ? "generate idea for me" : "no specific idea yet"),
        experienced_problem: answers.experienced_problem === "true",
        major_skillset: answers.major_skillset || "",
        unfair_advantage: parseInt(answers.unfair_advantage) || 1,
        startup_experience: answers.startup_experience || "no",
        worked_with_founders: answers.worked_with_founders === "true",
        runway_months: parseInt(answers.runway_months) || 0,
        debt_level: answers.debt_level || "none",
        has_dependents: answers.has_dependents === "true",
        can_raise_from_network: answers.can_raise_from_network === "true",
        network_strength: parseInt(answers.network_strength) || 1,
        emotional_stability: parseInt(answers.emotional_stability) || 1,
        comfort_with_uncertainty: parseInt(answers.comfort_with_uncertainty) || 1,
      };

      const response = await axios.post(`${API}/evaluate`, payload);
      
      // Navigate to results with data
      navigate("/results", { state: { result: response.data } });
    } catch (error) {
      console.error("Evaluation failed:", error);
      alert("Something went wrong with the AI evaluation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!currentQuestion) {
    return null;
  }

  const currentValue = getCurrentValue();

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden flex flex-col justify-between">
      {/* Background Gradients & Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#0f0f12_1px,transparent_1px),linear-gradient(to_bottom,#0f0f12_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
      <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-[#FF6B3D]/10 rounded-full blur-[100px] opacity-60 pointer-events-none"></div>

      {/* Top Navbar */}
      <header className="relative z-10 w-full max-w-7xl mx-auto px-6 py-6 border-b border-white/5 backdrop-blur-md bg-[#050505]/40 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate("/")}
            className="bg-[#FF6B3D] text-black font-['Plus_Jakarta_Sans'] font-black text-xl px-4 py-1.5 rounded-lg shadow-[0_0_15px_rgba(255,107,61,0.25)] hover:bg-[#FF5522] transition-colors"
            data-testid="home-link"
          >
            byeC
          </button>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs font-['JetBrains_Mono'] text-gray-500 uppercase tracking-widest">
            Step {currentStep + 1} of {activeQuestions.length}
          </span>
        </div>
      </header>

      {/* Progress Bar (Dynamic) */}
      <div className="w-full bg-white/5 h-1 relative z-10">
        <div 
          className="h-full bg-gradient-to-r from-[#FF6B3D] to-indigo-500 transition-all duration-300 shadow-[0_0_8px_#FF6B3D]"
          style={{ width: `${progress}%` }}
          data-testid="progress-bar"
        />
      </div>

      {/* Question Card Area */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-2xl w-full" data-testid="question-container">
          <div className="p-8 md:p-10 rounded-3xl border border-white/5 bg-[#0a0a0c]/60 backdrop-blur-xl shadow-[0_12px_40px_rgba(0,0,0,0.5)] space-y-8">
            
            {/* Category Badge */}
            <div className="space-y-2">
              <span className="inline-block text-xs font-['JetBrains_Mono'] font-bold text-[#FF6B3D] uppercase tracking-widest bg-[#FF6B3D]/10 px-3 py-1 rounded-md">
                {currentQuestion.category}
              </span>
              <h2 className="font-['Plus_Jakarta_Sans'] font-bold text-2xl md:text-3xl leading-snug text-white">
                {currentQuestion.question}
              </h2>
            </div>

            {/* Input Types */}
            <div className="pt-2">
              {currentQuestion.type === "number" && (
                <div className="relative">
                  <Input
                    type="number"
                    placeholder={currentQuestion.placeholder}
                    value={answers[currentQuestion.id] || ""}
                    onChange={(e) => handleAnswer(e.target.value)}
                    className="h-14 text-lg text-white bg-white/[0.02] border-white/10 rounded-xl focus:border-[#FF6B3D] focus:ring-1 focus:ring-[#FF6B3D] placeholder:text-gray-600 font-['JetBrains_Mono']"
                    data-testid={`input-${currentQuestion.id}`}
                  />
                </div>
              )}

              {currentQuestion.type === "textarea" && (
                <Textarea
                  placeholder={currentQuestion.placeholder}
                  value={answers[currentQuestion.id] || ""}
                  onChange={(e) => handleAnswer(e.target.value)}
                  className="min-h-32 text-lg text-white bg-white/[0.02] border-white/10 rounded-xl focus:border-[#FF6B3D] focus:ring-1 focus:ring-[#FF6B3D] placeholder:text-gray-600 leading-relaxed"
                  data-testid={`textarea-${currentQuestion.id}`}
                />
              )}

              {currentQuestion.type === "radio" && (
                <RadioGroup
                  value={answers[currentQuestion.id] || ""}
                  onValueChange={handleAnswer}
                  className="space-y-3"
                  data-testid={`radio-group-${currentQuestion.id}`}
                >
                  {currentQuestion.options.map((option) => {
                    const isSelected = answers[currentQuestion.id] === option.value;
                    return (
                      <div
                        key={option.value}
                        className={`flex items-center space-x-3 p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
                          isSelected
                            ? "border-[#FF6B3D] bg-[#FF6B3D]/5 shadow-[0_0_15px_rgba(255,107,61,0.08)]"
                            : "border-white/5 bg-white/[0.01] hover:border-white/10 hover:bg-white/[0.03]"
                        }`}
                        onClick={() => handleAnswer(option.value)}
                      >
                        <RadioGroupItem value={option.value} id={option.value} className="text-[#FF6B3D] border-white/20" />
                        <Label
                          htmlFor={option.value}
                          className="flex-1 cursor-pointer text-base text-gray-200 font-medium"
                        >
                          {option.label}
                        </Label>
                      </div>
                    );
                  })}
                </RadioGroup>
              )}

              {currentQuestion.type === "slider" && (
                <div className="space-y-6 pt-4" data-testid={`slider-${currentQuestion.id}`}>
                  <div className="flex items-center justify-between text-sm text-gray-500 font-['JetBrains_Mono']">
                    <span>min ({currentQuestion.min})</span>
                    <span className="text-4xl text-[#FF6B3D] font-bold">
                      {currentValue || currentQuestion.min}
                    </span>
                    <span>max ({currentQuestion.max})</span>
                  </div>
                  
                  <Slider
                    min={currentQuestion.min}
                    max={currentQuestion.max}
                    step={1}
                    value={[parseInt(currentValue) || currentQuestion.min]}
                    onValueChange={handleSliderChange}
                    className="w-full cursor-pointer py-4"
                  />
                  
                  {currentQuestion.labels && (
                    <div className="p-4 rounded-xl bg-white/[0.01] border border-white/5 space-y-2.5 text-xs text-gray-400 italic">
                      <div className="flex items-start gap-2">
                        <span className="font-bold text-white font-['JetBrains_Mono']">1:</span>
                        <span>{currentQuestion.labels[1]}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-bold text-white font-['JetBrains_Mono']">5:</span>
                        <span>{currentQuestion.labels[5]}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-bold text-white font-['JetBrains_Mono']">10:</span>
                        <span>{currentQuestion.labels[10]}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>
      </main>

      {/* Navigation Footer */}
      <footer className="relative z-10 w-full p-6 border-t border-white/5 bg-[#050505]/80 backdrop-blur-lg flex flex-col items-center">
        <div className="max-w-2xl w-full flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="text-gray-400 hover:text-white hover:bg-white/5 rounded-xl px-4 py-2"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            back
          </Button>

          <Button
            onClick={handleNext}
            disabled={!canProceed() || loading}
            className="h-12 px-8 bg-[#FF6B3D] hover:bg-[#FF5522] text-black font-semibold rounded-xl active:scale-95 transition-all shadow-[0_4px_20px_rgba(255,107,61,0.15)]"
            data-testid="next-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                evaluating your readiness...
              </>
            ) : currentStep === activeQuestions.length - 1 ? (
              <>
                get results
                <ChevronRight className="w-5 h-5 ml-1" />
              </>
            ) : (
              <>
                next
                <ChevronRight className="w-5 h-5 ml-1" />
              </>
            )}
          </Button>
        </div>
        
        {/* Credit */}
        <p className="text-[10px] text-gray-600 text-center mt-4">
          made by ishan sukul aka <a href="https://twitter.com/isawsukul" target="_blank" rel="noopener noreferrer" className="text-[#FF6B3D]/70 hover:underline">@isawsukul</a>
        </p>
      </footer>
    </div>
  );
}
